import { useEffect, useMemo, useState } from "react";
import { Search } from "lucide-react";
import { api, fmtL, ROLES } from "@/lib/api";
import { PlayerAvatar, Spinner, EmptyState, Badge } from "@/components/opl/Common";
import { Input } from "@/components/ui/input";

const STATUS_L = {
  upcoming: "text-blue-700 border-blue-200 bg-blue-50", live: "text-red-700 border-red-200 bg-red-50", sold: "text-emerald-700 border-emerald-200 bg-emerald-50",
  unsold: "text-slate-600 border-slate-200 bg-slate-50", withdrawn: "text-orange-700 border-orange-200 bg-orange-50",
};

const Chip = ({ active, onClick, children, testid }) => (
  <button onClick={onClick} data-testid={testid} className={`px-3.5 py-1.5 rounded-full text-xs font-medium border capitalize transition-colors ${active ? "border-[#0F172A] bg-[#0F172A] text-white" : "border-[#E7E5E0] bg-white text-slate-600 hover:border-slate-400"}`}>{children}</button>
);

function PlayerCard({ p }) {
  return (
    <div className="card-l p-5" data-testid={`player-card-${p.code}`}>
      <div className="flex justify-between items-start">
        <PlayerAvatar player={p} size={64} />
        <div className="flex flex-col items-end gap-1">
          <Badge className={STATUS_L[p.auction_status]}>{p.auction_status}</Badge>
          {p.lot_no != null && <span className="num text-xs font-semibold text-slate-400">#{p.lot_no}</span>}
        </div>
      </div>
      <h3 className="font-semibold text-lg mt-4 truncate text-[#0F172A]">{p.name}</h3>
      <p className="text-xs text-slate-500">{p.role} · {p.category_name}</p>
      <p className="text-xs text-slate-400 mt-1 truncate">{[p.batting_style, p.bowling_style].filter(Boolean).join(" · ") || "—"}</p>
      <div className="mt-4 pt-3 border-t border-[#F0EEE8] flex justify-between text-sm">
        {p.auction_status === "sold" ? <><span className="text-slate-600 truncate">{p.team_name}</span><b className="num text-amber-700">{fmtL(p.sold_price)}</b></> :
          <><span className="text-slate-400">Base</span><b className="num text-[#0F172A]">{fmtL(p.effective_base_price)}</b></>}
      </div>
    </div>
  );
}

export default function PublicPlayers() {
  const [d, setD] = useState(null);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [role, setRole] = useState("");
  const [status, setStatus] = useState("");
  useEffect(() => { api.get("/public/players").then((r) => setD(r.data)); }, []);
  const list = useMemo(() => (d?.players || []).filter((p) =>
    (!q || p.name.toLowerCase().includes(q.toLowerCase())) && (!cat || p.category_id === cat) && (!role || p.role === role) && (!status || p.auction_status === status)), [d, q, cat, role, status]);
  if (!d) return <Spinner />;
  return (
    <div className="max-w-7xl mx-auto px-5 lg:px-8 py-14">
      <p className="eyebrow">Auction pool</p>
      <h1 className="text-4xl sm:text-5xl font-bold mt-2 text-[#0F172A]">Players <span className="text-slate-300 num">{list.length}</span></h1>
      <div className="mt-8 space-y-3">
        <div className="relative max-w-md">
          <Search className="absolute left-3.5 top-3.5 h-4 w-4 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search players" className="pl-10 h-11 rounded-full bg-white border-[#E7E5E0] text-[#0F172A]" data-testid="player-search-input" />
        </div>
        {d.categories.length > 1 && (
          <div className="flex flex-wrap gap-2">
            <Chip active={!cat} onClick={() => setCat("")} testid="filter-cat-all">All categories</Chip>
            {d.categories.map((c) => <Chip key={c.id} active={cat === c.id} onClick={() => setCat(c.id)} testid={`filter-cat-${c.name}`}>{c.name}</Chip>)}
          </div>
        )}
        <div className="flex flex-wrap gap-2">
          <Chip active={!role} onClick={() => setRole("")} testid="filter-role-all">All roles</Chip>
          {ROLES.map((r) => <Chip key={r} active={role === r} onClick={() => setRole(r)} testid={`filter-role-${r}`}>{r}</Chip>)}
          <span className="w-px bg-[#E7E5E0] mx-1" />
          {["upcoming", "live", "sold", "unsold", "withdrawn"].map((s) => <Chip key={s} active={status === s} onClick={() => setStatus(status === s ? "" : s)} testid={`filter-status-${s}`}>{s}</Chip>)}
        </div>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-8" data-testid="public-players-grid">
        {list.map((p) => <PlayerCard key={p.id} p={p} />)}
      </div>
      {!list.length && <div className="mt-8"><EmptyState title="No players match" /></div>}
    </div>
  );
}
